// ---------------------------------------------------------------------------
// The aggregated reveal: a round drawn as a distribution rather than as a list
// of people.
//
// **The answer is the subject and the players are the detail.** That inversion
// is the whole screen, and it is argued out on `AggregateEntry` in
// `@countdown/shared`: two hundred rows each saying one person's word is not a
// slower version of this, it is a different and worse thing. So a row is a word,
// and everybody who found it is gathered behind it — `ALICE, BOB and 12 others`.
//
// Everything about WHEN a row is allowed on the screen is in `aggregate.ts`,
// including the pacing argument. This file draws what that decides and holds no
// state of its own, the same as every other frame on this stage.
//
// Four things here are decisions rather than layout:
//
//   **The rows are ranked by score, and the count is beside them.** Popularity
//   and quality disagree — the word forty people found is usually a short one —
//   and `RoundAggregate.top` settles which one orders the table. Popularity is
//   still on every row and is the whole of the histogram on the next slide; it
//   simply does not decide what is at the top.
//
//   **Every ruled-out answer is one row.** Isaac: invalid words need lumping
//   together, because a rejected word per row is what makes a big round
//   unreadable. They are still named, still struck through and still askable —
//   what they lose is a row each and a score column each, which said `0` a dozen
//   times over.
//
//   **The strike is `line-through` and it stays that way.** CLAUDE.md: the
//   askable-word mark is an inset shadow precisely because `underline` and
//   `line-through` are one CSS property, and the first cut of that silently
//   removed the strikethrough from every ruled-out word. The reveal draws no
//   askable mark at all (`AskableWord` passes `marked={false}`), so the only
//   thing using the property here is the ruling — and it must stay the only one.
//
//   **Nothing that is not in the eight is dropped.** `otherAnswers`,
//   `otherPlayers` and `noAnswer` are a line under the table, because a table
//   showing eight of forty answers with nothing saying so is a silent cap.
// ---------------------------------------------------------------------------

import type { AggregateEntry, ClientGameState } from '@countdown/shared';
import { Frame } from '../../../components/round/atoms.js';
import { AskableWord, ReasonChip, STAGE_FILL } from './atoms.js';
import {
  aggregateOf,
  findersLine,
  noAnswerLine,
  otherAnswersLine,
  playersBehind,
  releasedAggregateRows,
  splitByRuling,
} from './aggregate.js';
import { answerTextOf } from './ruling.js';
import type { RevealView } from './view.js';

/**
 * Whether a word on this round is the sort of thing a dictionary has an entry
 * for. A numbers answer is an expression, and asking what `(75 - 3) * 7` means
 * is a control with one outcome.
 */
function wordsAreAskable(view: RevealView): boolean {
  return view.round.type !== 'numbers';
}

/** The viewer's own answer this round, whether or not it made the eight. */
function ownAnswerOf(state: ClientGameState, view: RevealView): string {
  const own = view.round.submissions[state.youId];
  if (own === undefined) return '';
  return answerTextOf(own).toUpperCase();
}

function Word({
  view,
  answer,
  out,
  className,
}: {
  view: RevealView;
  answer: string;
  out: boolean;
  className: string;
}) {
  // The strike is drawn by the word's own text run, so it moves with the glyphs
  // and can never cover a neighbour — the rule at the head of `RevealScreen.tsx`
  // that nothing is drawn OVER an answer. `skip-ink: none` so the line crosses
  // descenders rather than breaking around them, which at this weight reads as a
  // dropped stroke.
  const face = `min-w-0 truncate font-extrabold tracking-[0.06em] text-ink ${className} ${
    out ? 'line-through decoration-alert/80 decoration-[3px] [text-decoration-skip-ink:none]' : ''
  }`;

  if (!wordsAreAskable(view)) return <span className={face}>{answer}</span>;
  return (
    <AskableWord view={view} word={answer} className={face}>
      {answer}
    </AskableWord>
  );
}

/**
 * The good answers, best first, each with the room that found it behind it.
 */
function StoodRow({
  view,
  entry,
  spotlit,
  mine,
}: {
  view: RevealView;
  entry: AggregateEntry;
  spotlit: boolean;
  mine: boolean;
}) {
  const answer = entry.answer.toUpperCase();

  return (
    <li
      data-answer={answer}
      data-count={entry.count}
      data-spotlit={spotlit ? 'true' : undefined}
      data-mine={mine ? 'true' : undefined}
      // The border is on every row, transparent when it says nothing, so a row
      // becoming the newest cannot move the rows under it by two pixels. The
      // `you` edge is an inset shadow rather than a border for the same reason,
      // and it stacks with the spotlight fill instead of replacing it.
      className={`flex items-center gap-3 rounded-card border px-3 py-1.5 transition-colors duration-300 ease-out ${
        spotlit ? 'border-blue-500/60 bg-blue-700/35' : 'border-transparent bg-surface-2'
      } ${mine ? 'shadow-[inset_0_0_0_2px_var(--c-accent)]' : ''}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-2">
          <Word view={view} answer={answer} out={false} className="text-[17px] sm:text-[20px]" />
          {/* How many found it, said as a number rather than as a bar. The bar
              for this is the histogram on the next slide, over the whole room
              rather than over eight rows of it. */}
          <span className="tabular shrink-0 font-mono text-[11px] text-ink-dim">
            &times;{entry.count}
          </span>
        </div>
        <span className="truncate font-mono text-[11px] text-ink-dim">
          {findersLine(view.state, entry)}
        </span>
      </div>

      <span
        className={`tabular shrink-0 font-mono text-[18px] font-bold sm:text-[20px] ${
          entry.score > 0 ? 'text-good' : 'text-ink-dim'
        }`}
      >
        {entry.score}
      </span>
    </li>
  );
}

/**
 * Every ruled-out answer in the eight, in one row.
 *
 * The count leads, because that is the fact the room is owed — words WERE ruled
 * out and here is how many — and the words follow it as a list rather than as
 * rows. They wrap; the row grows a line rather than truncating a word away,
 * since a rejected word its author cannot read is the one thing the reveal has
 * never been allowed to do.
 */
function RuledOutRow({ view, rows }: { view: RevealView; rows: AggregateEntry[] }) {
  if (rows.length === 0) return null;
  const players = playersBehind(rows);

  return (
    <li
      data-ruled-out={rows.length}
      className="flex flex-col gap-1 rounded-card border border-transparent bg-surface-2 px-3 py-1.5"
    >
      <div className="flex items-center gap-2">
        <ReasonChip tone="alert">
          {rows.length === 1 ? '1 ruled out' : `${rows.length} ruled out`}
        </ReasonChip>
        <span className="font-mono text-[11px] text-ink-dim">
          {players === 1 ? '1 player' : `${players} players`}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
        {rows.map((entry) => (
          <Word
            key={entry.answer}
            view={view}
            answer={entry.answer.toUpperCase()}
            out
            className="text-[14px]"
          />
        ))}
      </div>
    </li>
  );
}

/**
 * The round, as a distribution. The stage's main slide for an aggregated reveal.
 */
export function AggregateBoard({ view }: { view: RevealView }) {
  const aggregate = aggregateOf(view.round);
  if (aggregate === null) return null;

  const rows = releasedAggregateRows(view.round);
  const { stood, ruledOut } = splitByRuling(rows);
  const own = ownAnswerOf(view.state, view);
  // The newest row is the first one, because the table fills worst-first and
  // `releasedAggregateRows` hands them back best-first. Nothing has to be
  // remembered between renders to know which one just landed.
  const newest = stood[0]?.answer.toUpperCase() ?? null;

  const others = otherAnswersLine(aggregate);
  const nothing = noAnswerLine(aggregate);
  // Your own answer is always in `submissions` — see `ClientRound.aggregate` —
  // so a player whose word did not make the eight is still told what they
  // played, on the one screen that would otherwise never mention it.
  const ownIsOnTheTable = own.length > 0 && rows.some((row) => row.answer.toUpperCase() === own);

  return (
    <Frame dense className={STAGE_FILL}>
      <header className="flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-lg font-bold sm:text-xl">
          {view.round.type === 'letters' ? 'The words' : 'The answers'}
        </h2>
        <span className="label-mono ml-auto">best first</span>
      </header>

      <ul
        aria-label="The answers, best first, and who found them"
        className="flex flex-col gap-1.5"
      >
        {stood.map((entry) => {
          const answer = entry.answer.toUpperCase();
          return (
            <StoodRow
              key={answer}
              view={view}
              entry={entry}
              spotlit={answer === newest}
              mine={own.length > 0 && answer === own}
            />
          );
        })}
        <RuledOutRow view={view} rows={ruledOut} />
      </ul>

      {/* Everything the eight rows do not account for. It is a footer rather
          than a row, because these are not answers — they are the rest of the
          room, and a row for them in a list of words would read as one. */}
      {others === null && nothing === null && ownIsOnTheTable ? null : (
        <div className="mt-auto flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-2 font-mono text-[11px] text-ink-dim">
          {others === null ? null : <span data-other-answers="">{others}</span>}
          {nothing === null ? null : <span data-no-answer="">{nothing}</span>}
          {ownIsOnTheTable || own.length === 0 ? null : (
            <span data-your-answer="" className="ml-auto">
              yours: {own}
            </span>
          )}
        </div>
      )}
    </Frame>
  );
}
